import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminCourseKeys, courseKeys } from "@/lib/query";

export type AdminCourse = {
  id: string;
  slug: string;
  title: string;
  description: string;
  totalModules: number;
  isPublished: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  previewUrl: string | null;
};

async function fetchAdminCourses(): Promise<AdminCourse[]> {
  const res = await fetch("/api/admin/courses");
  if (!res.ok) throw new Error("Failed to load courses");
  const json = (await res.json()) as { data: AdminCourse[] };
  return json.data;
}

export function useAdminCourses() {
  return useQuery({
    queryKey: adminCourseKeys.list(),
    queryFn: fetchAdminCourses,
  });
}

export function useToggleCoursePublished() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ courseId, isPublished }: { courseId: string; isPublished: boolean }) => {
      const res = await fetch(`/api/admin/courses/${courseId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPublished }),
      });
      if (!res.ok) throw new Error("Failed to update course");
    },
    onSuccess: (_data, { isPublished }) => {
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.all });
      queryClient.invalidateQueries({ queryKey: courseKeys.all });
      toast.success(isPublished ? "Course published" : "Course unpublished");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update course");
    },
  });
}
